import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { FaUserShield, FaKey, FaUser, FaSave, FaShieldAlt, FaEye, FaEyeSlash, FaExclamationTriangle } from "react-icons/fa";

export default function AdminChangePassword() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = (data) => {
    setPendingData(data);
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!pendingData) return;
    setIsConfirmOpen(false);

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.put(
        `${server}/auth/changePasswordForAdmin`,
        {
          username: pendingData.username,
          newPassword: pendingData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        toast.success(response.data.message || "Password changed successfully");
        reset();
        setPendingData(null);
      }
    } catch (error) {
      console.error("Error changing password:", error);
      const errorMsg = error.response?.data?.message || error.response?.data || "Failed to change password";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="min-h-screen p-4 md:p-8 flex items-center justify-center bg-slate-50/50">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-lg"
      >
        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-blue-100/50 border border-blue-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-10 text-white relative">
            <div className="absolute -right-6 -bottom-6 p-6 opacity-10">
              <FaShieldAlt className="text-9xl rotate-12" />
            </div>
            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-md border border-white/30 shadow-xl">
                <FaUserShield className="text-3xl text-white" />
              </div>
              <h1 className="text-3xl font-black tracking-tight">Admin Controls</h1>
              <div className="h-1 w-12 bg-white/40 rounded-full my-3"></div>
              <p className="text-blue-100 text-sm font-medium">System-wide Security Override</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-10 space-y-8">
            <div className="space-y-6">
              {/* Username Field */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] ml-1">
                  Target Username
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors duration-300">
                    <FaUser className="text-sm" />
                  </div>
                  <input
                    type="text"
                    {...register("username", { required: "Username is required" })}
                    placeholder="Enter user identity"
                    className="w-full pl-12 pr-4 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all duration-300 text-slate-700 font-medium placeholder:text-slate-400"
                  />
                </div>
                {errors.username && (
                  <p className="text-rose-500 text-[10px] font-bold ml-1 uppercase tracking-wider animate-pulse">
                    {errors.username.message}
                  </p>
                )}
              </div>

              {/* New Password Field */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] ml-1">
                  New Credential
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors duration-300">
                    <FaKey className="text-sm" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    {...register("newPassword", {
                      required: "New password is required",
                      minLength: { value: 6, message: "Minimum 6 characters required" },
                    })}
                    placeholder="Enter strong password"
                    className="w-full pl-12 pr-12 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all duration-300 text-slate-700 font-medium placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors duration-300"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="text-rose-500 text-[10px] font-bold ml-1 uppercase tracking-wider animate-pulse">
                    {errors.newPassword.message}
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-blue-700 transition-all duration-300 shadow-xl shadow-blue-200 disabled:opacity-70 disabled:cursor-not-allowed group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <FaSave className="text-sm group-hover:scale-110 transition-transform" />
                  Apply Update
                </>
              )}
            </button>
          </form>

          {/* Security Banner */}
          <div className="px-10 py-6 bg-slate-50 border-t border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-200">
              <FaShieldAlt className="text-blue-600 text-sm" />
            </div>
            <div>
              <p className="text-[11px] text-slate-500 leading-tight">
                <span className="font-bold text-slate-800 uppercase tracking-tighter">Security Protocol:</span> This action is logged. Unauthorized password resets are strictly prohibited.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {isConfirmOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsConfirmOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
            ></motion.div>

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden z-10"
            >
              <div className="bg-amber-500 p-6 flex justify-center">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center border border-white/30 backdrop-blur-sm">
                  <FaExclamationTriangle className="text-3xl text-white" />
                </div>
              </div>

              <div className="p-8 text-center">
                <h3 className="text-xl font-black text-slate-900 mb-2">Confirm Reset</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-8">
                  You are about to force-reset the password for <span className="font-bold text-blue-600">"{pendingData?.username}"</span>. This action cannot be undone.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setIsConfirmOpen(false)}
                    className="flex-1 px-6 py-3.5 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    className="flex-1 px-6 py-3.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
